import { useState, useRef, useEffect, forwardRef } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { toPng, toBlob } from "html-to-image";
import { jsPDF } from "jspdf";
import QRCode from "qrcode";
import { Download, FileImage, Share2, Star, MessageSquareQuote, MailOpen, Sparkles, CreditCard, Phone, Mail, Globe, Linkedin, Instagram } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import DashboardShell from "@/components/DashboardShell";

const A4_W = 794;   // 210mm @ 96dpi
const A4_H = 1123;  // 297mm @ 96dpi
const CARD_W = 1050;  // Landscape social card
const CARD_H = 600;

export default function Studio() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("testimonial");

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-ivory"><div className="w-10 h-10 border-2 border-aubergine/20 border-t-aubergine rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <DashboardShell role={user.role === "admin" ? "admin" : "member"} title="Design Studio" subtitle="Create beautiful, shareable testimonials, invitations & business cards">
      <div data-testid="studio-page">
        <div className="flex flex-wrap gap-2 mb-6">
          <TabBtn active={tab === "testimonial"} onClick={() => setTab("testimonial")} icon={MessageSquareQuote} testid="studio-tab-testimonial">Testimonial</TabBtn>
          <TabBtn active={tab === "invitation"} onClick={() => setTab("invitation")} icon={MailOpen} testid="studio-tab-invitation">Invitation Flyer</TabBtn>
          <TabBtn active={tab === "card"} onClick={() => setTab("card")} icon={CreditCard} testid="studio-tab-card">Business Card</TabBtn>
        </div>

        {tab === "testimonial" && <TestimonialStudio user={user} />}
        {tab === "invitation" && <InvitationStudio user={user} />}
        {tab === "card" && <BusinessCardStudio user={user} />}
      </div>
    </DashboardShell>
  );
}

function TabBtn({ active, onClick, children, icon: Icon, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs uppercase tracking-[0.14em] border transition-colors ${
        active ? "bg-aubergine text-ivory border-aubergine" : "border-aubergine/20 text-plum hover:bg-aubergine/[0.04]"
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {children}
    </button>
  );
}

/* ============================== TESTIMONIAL ============================== */

function TestimonialStudio({ user }) {
  const [form, setForm] = useState({
    author_name: user.name || "",
    author_role: user.business || "",
    provider_name: "",
    service: "",
    rating: 5,
    quote: "",
    photo: user.avatar || "",
  });
  const canvasRef = useRef(null);

  return (
    <div className="grid lg:grid-cols-[400px_1fr] gap-8">
      {/* Form */}
      <div className="bg-white rounded-2xl border border-aubergine/[0.08] p-6 h-fit">
        <h3 className="text-[13px] uppercase tracking-[0.14em] text-plumSoft/70 mb-5 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-champagne" /> Testimonial Details
        </h3>

        <Field label="Your Name">
          <input data-testid="tm-author" value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} className="input-luxe" placeholder="e.g. Priya Sharma" />
        </Field>

        <Field label="Your Business / Role">
          <input data-testid="tm-role" value={form.author_role} onChange={(e) => setForm({ ...form, author_role: e.target.value })} className="input-luxe" placeholder="e.g. Founder · Aurora Tech" />
        </Field>

        <Field label="Provider / Service Given By">
          <input data-testid="tm-provider" value={form.provider_name} onChange={(e) => setForm({ ...form, provider_name: e.target.value })} className="input-luxe" placeholder="e.g. Anaya Iyer · Iyer Wealth" />
        </Field>

        <Field label="Service or Product Taken">
          <input data-testid="tm-service" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="input-luxe" placeholder="e.g. Wealth Planning Consultation" />
        </Field>

        <Field label={`Rating (${form.rating} / 5)`}>
          <div className="flex items-center gap-1.5">
            {[1, 2, 3, 4, 5].map(n => (
              <button key={n} type="button" data-testid={`tm-star-${n}`} onClick={() => setForm({ ...form, rating: n })} className="p-1">
                <Star className={`w-6 h-6 transition-colors ${n <= form.rating ? "fill-champagne text-champagne" : "text-plumSoft/30"}`} />
              </button>
            ))}
          </div>
        </Field>

        <Field label="Testimonial Text">
          <textarea data-testid="tm-quote" rows={6} maxLength={500} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} className="input-luxe" placeholder="Share your genuine experience — what changed for you? What made this service exceptional?" />
          <p className="text-[10px] text-plumSoft/60 mt-1">{form.quote.length}/500 · Keep it warm and specific.</p>
        </Field>

        <Field label="Your Photo URL (optional)">
          <input data-testid="tm-photo" value={form.photo} onChange={(e) => setForm({ ...form, photo: e.target.value })} className="input-luxe" placeholder="Paste photo URL or leave blank for initials" />
        </Field>

        <ExportBar canvasRef={canvasRef} filenameBase={`testimonial-${(form.author_name || "wowshe").toLowerCase().replace(/\s+/g, "-")}`} shareCaption={`My testimonial for ${form.provider_name || "a WOWSHE member"} — read below.`} />
      </div>

      {/* Preview */}
      <PreviewFrame>
        <TestimonialCanvas ref={canvasRef} data={form} />
      </PreviewFrame>
    </div>
  );
}

const TestimonialCanvas = wrapRef(function TestimonialCanvasInner({ data }, ref) {
  const initials = (data.author_name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div
      ref={ref}
      style={{
        width: A4_W, height: A4_H,
        background: "#FDFBF7",
        fontFamily: '"Outfit", sans-serif',
        color: "#3A0F4D",
        position: "relative",
        overflow: "hidden",
        padding: "72px 80px",
        boxSizing: "border-box",
      }}
    >
      {/* Ornamental rose-gold double frame */}
      <div style={{ position: "absolute", inset: 40, border: "1px solid #D9A55C", pointerEvents: "none" }} />
      <div style={{ position: "absolute", inset: 48, border: "1px solid rgba(217,165,92,0.35)", pointerEvents: "none" }} />

      {/* Top decorative flourish */}
      <div style={{ position: "absolute", top: 56, left: "50%", transform: "translateX(-50%)", display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ width: 40, height: 1, background: "#D9A55C" }} />
        <span style={{ fontSize: 10, letterSpacing: "0.35em", color: "#9D2B6B", textTransform: "uppercase" }}>Testimonial</span>
        <div style={{ width: 40, height: 1, background: "#D9A55C" }} />
      </div>

      {/* Logo */}
      <div style={{ textAlign: "center", marginTop: 22 }}>
        <img src="/wowshe-logo.png" crossOrigin="anonymous" alt="WOWSHE" style={{ height: 64, width: "auto", display: "inline-block" }} />
      </div>

      {/* HIGHLIGHTED RECIPIENT — the person/business being praised */}
      <div style={{ textAlign: "center", marginTop: 26 }}>
        <p style={{ margin: 0, fontSize: 11, letterSpacing: "0.32em", textTransform: "uppercase", color: "#6B4769" }}>
          A Testimonial for
        </p>
        <p style={{
          margin: "10px 0 0",
          fontFamily: '"Playfair Display", serif',
          fontSize: 44,
          lineHeight: 1.1,
          color: "#3A0F4D",
          fontWeight: 600,
          letterSpacing: "-0.005em",
        }}>
          {data.provider_name || "Service Provider Name"}
        </p>
        {data.service && (
          <div style={{ marginTop: 14 }}>
            <span style={{ display: "inline-block", padding: "9px 22px", background: "rgba(217,165,92,0.14)", border: "1px solid rgba(217,165,92,0.5)", borderRadius: 100, fontSize: 12, letterSpacing: "0.22em", textTransform: "uppercase", color: "#9D2B6B", fontWeight: 500 }}>
              {data.service}
            </span>
          </div>
        )}
      </div>

      {/* Giant opening quote */}
      <div style={{ textAlign: "center", marginTop: 30 }}>
        <span style={{ fontFamily: '"Playfair Display", serif', fontSize: 110, lineHeight: 0.7, color: "#D9A55C", fontStyle: "italic" }}>&ldquo;</span>
      </div>

      {/* Quote body */}
      <div style={{ textAlign: "center", padding: "18px 60px 0" }}>
        <p style={{
          fontFamily: '"Playfair Display", serif',
          fontSize: 24,
          lineHeight: 1.55,
          textAlign: "center",
          fontStyle: "italic",
          color: "#3A0F4D",
          margin: "0 auto",
          maxWidth: 560,
        }}>
          {data.quote || "Share a heartfelt sentence or two about the experience — how it helped you, what made it stand out, and why you'd recommend it to another woman."}
        </p>
      </div>

      {/* Stars */}
      <div style={{ textAlign: "center", marginTop: 26 }}>
        {[1, 2, 3, 4, 5].map(n => (
          <span key={n} style={{ fontSize: 28, color: n <= data.rating ? "#D9A55C" : "rgba(107,71,105,0.25)", margin: "0 3px" }}>★</span>
        ))}
      </div>

      {/* FOOTER — compact author attribution + brand tagline */}
      <div style={{
        position: "absolute",
        left: 80, right: 80, bottom: 68,
        borderTop: "1px solid rgba(217,165,92,0.4)",
        paddingTop: 20,
      }}>
        {/* Author row */}
        <table style={{ margin: "0 auto 14px", borderCollapse: "collapse" }}>
          <tbody>
            <tr>
              <td style={{ verticalAlign: "middle", paddingRight: 14 }}>
                {data.photo ? (
                  <img
                    src={data.photo}
                    crossOrigin="anonymous"
                    alt=""
                    style={{ width: 52, height: 52, borderRadius: "50%", objectFit: "cover", border: "2px solid #D9A55C", display: "block" }}
                  />
                ) : (
                  <div style={{
                    width: 52, height: 52, borderRadius: "50%",
                    background: "linear-gradient(135deg,#9D2B6B,#3A0F4D)",
                    color: "#FDFBF7",
                    textAlign: "center", lineHeight: "52px",
                    fontFamily: '"Playfair Display", serif', fontSize: 20,
                    border: "2px solid #D9A55C",
                  }}>{initials}</div>
                )}
              </td>
              <td style={{ verticalAlign: "middle", textAlign: "left" }}>
                <p style={{ margin: 0, fontSize: 9, letterSpacing: "0.28em", textTransform: "uppercase", color: "#9D2B6B" }}>Shared by</p>
                <p style={{ margin: "3px 0 0", fontFamily: '"Playfair Display", serif', fontSize: 17, color: "#3A0F4D", lineHeight: 1.15 }}>{data.author_name || "Your Name"}</p>
                <p style={{ margin: "2px 0 0", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "#6B4769" }}>{data.author_role || "Founder · Business"}</p>
              </td>
            </tr>
          </tbody>
        </table>

        {/* Brand tagline */}
        <div style={{ textAlign: "center" }}>
          <p style={{ margin: 0, fontSize: 9, letterSpacing: "0.35em", textTransform: "uppercase", color: "#6B4769" }}>
            Women Of Wisdom · Success · Happiness · Empowerment
          </p>
          <p style={{ margin: "5px 0 0", fontSize: 10, color: "#9D2B6B", letterSpacing: "0.1em" }}>wowshe.in</p>
        </div>
      </div>
    </div>
  );
});

/* ============================== INVITATION FLYER ============================== */

function InvitationStudio({ user }) {
  const [form, setForm] = useState({
    visitor_name: "",
    host_name: user.name || "",
    host_chapter: user.chapter || "",
    venue: "",
    date: "",
    time: "",
    occasion: "WOWSHE Chapter Meetup",
    rsvp: user.phone || "",
  });
  const canvasRef = useRef(null);

  return (
    <div className="grid lg:grid-cols-[400px_1fr] gap-8">
      {/* Form */}
      <div className="bg-white rounded-2xl border border-aubergine/[0.08] p-6 h-fit">
        <h3 className="text-[13px] uppercase tracking-[0.14em] text-plumSoft/70 mb-5 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-champagne" /> Invitation Details
        </h3>

        <Field label="Visitor Name (Your Guest)">
          <input data-testid="inv-visitor" value={form.visitor_name} onChange={(e) => setForm({ ...form, visitor_name: e.target.value })} className="input-luxe" placeholder="e.g. Nisha Kapoor" />
        </Field>

        <Field label="Occasion / Event Title">
          <input data-testid="inv-occasion" value={form.occasion} onChange={(e) => setForm({ ...form, occasion: e.target.value })} className="input-luxe" placeholder="WOWSHE Chapter Meetup" />
        </Field>

        <Field label="Venue">
          <input data-testid="inv-venue" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} className="input-luxe" placeholder="e.g. Taj Krishna, Banjara Hills, Hyderabad" />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Date">
            <input data-testid="inv-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="input-luxe" />
          </Field>
          <Field label="Time">
            <input data-testid="inv-time" type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="input-luxe" />
          </Field>
        </div>

        <Field label="Hosted / Invited By">
          <input data-testid="inv-host" value={form.host_name} onChange={(e) => setForm({ ...form, host_name: e.target.value })} className="input-luxe" placeholder="Your name" />
        </Field>

        <Field label="RSVP Contact (WhatsApp / Phone)">
          <input data-testid="inv-rsvp" value={form.rsvp} onChange={(e) => setForm({ ...form, rsvp: e.target.value })} className="input-luxe" placeholder="+91 98765 43210" />
        </Field>

        <ExportBar
          canvasRef={canvasRef}
          filenameBase={`invitation-${(form.visitor_name || "guest").toLowerCase().replace(/\s+/g, "-")}`}
          shareCaption={`✨ You're invited! ${form.occasion || "A WOWSHE gathering"}${form.date ? ` on ${form.date}` : ""}${form.venue ? ` at ${form.venue}` : ""}. Warmly, ${form.host_name || "WOWSHE"}`}
        />
      </div>

      {/* Preview */}
      <PreviewFrame>
        <InvitationCanvas ref={canvasRef} data={form} />
      </PreviewFrame>
    </div>
  );
}

const InvitationCanvas = wrapRef(function InvitationCanvasInner({ data }, ref) {
  const dateStr = data.date ? new Date(data.date + "T00:00:00").toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : "";
  const timeStr = data.time ? formatTime(data.time) : "";

  return (
    <div
      ref={ref}
      style={{
        width: A4_W, height: A4_H,
        background: "#FDFBF7",
        fontFamily: '"Outfit", sans-serif',
        color: "#3A0F4D",
        position: "relative",
        overflow: "hidden",
        display: "flex", flexDirection: "column",
      }}
    >
      {/* Top ivory band with logo */}
      <div style={{ padding: "56px 60px 24px", textAlign: "center" }}>
        <img src="/wowshe-logo.png" crossOrigin="anonymous" alt="WOWSHE" style={{ height: 68, width: "auto", display: "block", margin: "0 auto" }} />
        <p style={{ margin: "12px 0 0", fontSize: 10, letterSpacing: "0.35em", textTransform: "uppercase", color: "#6B4769" }}>
          Women Of Wisdom · Success · Happiness · Empowerment
        </p>
      </div>

      {/* Rose/champagne gradient panel */}
      <div style={{
        margin: "0 60px",
        background: "linear-gradient(160deg, #F5D4BF 0%, #F0B8A7 45%, #E7BC7E 100%)",
        borderRadius: 24,
        padding: "56px 48px",
        position: "relative",
        overflow: "hidden",
        flex: 1,
        display: "flex", flexDirection: "column",
      }}>
        {/* Decorative circles */}
        <div style={{ position: "absolute", top: -60, right: -60, width: 220, height: 220, borderRadius: "50%", background: "rgba(255,255,255,0.18)" }} />
        <div style={{ position: "absolute", bottom: -80, left: -60, width: 260, height: 260, borderRadius: "50%", background: "rgba(157,43,107,0.12)" }} />

        {/* "You're Invited" */}
        <div style={{ textAlign: "center", position: "relative", zIndex: 2 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
            <div style={{ width: 56, height: 1, background: "#3A0F4D" }} />
            <span style={{ fontSize: 11, letterSpacing: "0.4em", textTransform: "uppercase", color: "#3A0F4D", fontWeight: 500 }}>An Invitation</span>
            <div style={{ width: 56, height: 1, background: "#3A0F4D" }} />
          </div>
          <h1 style={{
            fontFamily: '"Playfair Display", serif',
            fontSize: 76,
            lineHeight: 1.05,
            fontWeight: 500,
            color: "#3A0F4D",
            margin: 0,
            letterSpacing: "-0.01em",
          }}>You&rsquo;re<br/>Invited</h1>
        </div>

        {/* Visitor name script */}
        <div style={{ textAlign: "center", marginTop: 40, position: "relative", zIndex: 2 }}>
          <p style={{ margin: 0, fontSize: 13, letterSpacing: "0.25em", textTransform: "uppercase", color: "#5E1380" }}>Dear</p>
          <p style={{
            margin: "10px 0 0",
            fontFamily: '"Playfair Display", serif',
            fontStyle: "italic",
            fontSize: 54,
            lineHeight: 1.1,
            color: "#9D2B6B",
            fontWeight: 500,
          }}>{data.visitor_name || "Cherished Guest"}</p>
        </div>

        {/* Occasion */}
        <div style={{ textAlign: "center", marginTop: 36, position: "relative", zIndex: 2 }}>
          <p style={{ margin: 0, fontSize: 12, letterSpacing: "0.25em", textTransform: "uppercase", color: "#5E1380" }}>To join us at</p>
          <p style={{
            margin: "8px 0 0",
            fontFamily: '"Playfair Display", serif',
            fontSize: 28,
            color: "#3A0F4D",
            fontWeight: 600,
          }}>{data.occasion || "WOWSHE Chapter Meetup"}</p>
        </div>

        {/* Details block */}
        <div style={{
          marginTop: 34,
          background: "rgba(253,251,247,0.85)",
          borderRadius: 16,
          padding: "22px 28px",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
          position: "relative",
          zIndex: 2,
        }}>
          <DetailBlock label="When" value={dateStr || "Date to be announced"} sub={timeStr} />
          <DetailBlock label="Where" value={data.venue || "Venue to be announced"} />
        </div>
      </div>

      {/* Bottom: host + RSVP */}
      <div style={{ padding: "26px 60px 40px", textAlign: "center" }}>
        <p style={{ margin: 0, fontSize: 11, letterSpacing: "0.35em", textTransform: "uppercase", color: "#6B4769" }}>Invited by</p>
        <p style={{ margin: "6px 0 0", fontFamily: '"Playfair Display", serif', fontSize: 22, color: "#3A0F4D" }}>
          {data.host_name || "A WOWSHE Sister"}
        </p>
        {data.host_chapter && (
          <p style={{ margin: "3px 0 0", fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "#9D2B6B" }}>{data.host_chapter} Chapter</p>
        )}
        {data.rsvp && (
          <div style={{ marginTop: 14, display: "inline-block", padding: "10px 26px", background: "#3A0F4D", color: "#FDFBF7", borderRadius: 100, fontSize: 12, letterSpacing: "0.22em", textTransform: "uppercase" }}>
            RSVP · {data.rsvp}
          </div>
        )}
      </div>
    </div>
  );
});

function DetailBlock({ label, value, sub }) {
  return (
    <div>
      <p style={{ margin: 0, fontSize: 10, letterSpacing: "0.28em", textTransform: "uppercase", color: "#9D2B6B", fontWeight: 500 }}>{label}</p>
      <p style={{ margin: "6px 0 0", fontFamily: '"Playfair Display", serif', fontSize: 16, color: "#3A0F4D", lineHeight: 1.35 }}>{value}</p>
      {sub && <p style={{ margin: "2px 0 0", fontSize: 12, color: "#6B4769" }}>{sub}</p>}
    </div>
  );
}

/* ============================== SHARED ============================== */

function Field({ label, children }) {
  return (
    <div className="mb-4">
      <label className="block text-[11px] uppercase tracking-[0.14em] text-plumSoft/70 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function PreviewFrame({ children, w = A4_W, h = A4_H, scale = 0.62 }) {
  return (
    <div className="bg-[#F3EFE9] rounded-2xl border border-aubergine/[0.08] p-6 flex items-start justify-center overflow-hidden">
      <div
        style={{
          width: w * scale,
          height: h * scale,
          position: "relative",
        }}
      >
        <div style={{ transform: `scale(${scale})`, transformOrigin: "top left", width: w, height: h }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function ExportBar({ canvasRef, filenameBase, shareCaption, width = A4_W, height = A4_H, pdfFormat = "a4", pdfOrientation = "portrait", showPdf = true }) {
  const [busy, setBusy] = useState(false);

  const waitForFonts = async () => {
    if (document.fonts && document.fonts.load) {
      try {
        await Promise.all([
          document.fonts.load('italic 26px "Playfair Display"'),
          document.fonts.load('500 22px "Playfair Display"'),
          document.fonts.load('500 76px "Playfair Display"'),
          document.fonts.load('italic 54px "Playfair Display"'),
          document.fonts.load('600 28px "Playfair Display"'),
        ]);
        if (document.fonts.ready) await document.fonts.ready;
      } catch { /* noop */ }
    }
    await new Promise(r => setTimeout(r, 300));
  };

  const renderPngDataUrl = async () => {
    const node = canvasRef.current;
    if (!node) throw new Error("Preview not ready");
    await waitForFonts();
    return await toPng(node, {
      cacheBust: true,
      pixelRatio: 2,
      width,
      height,
      backgroundColor: "#FDFBF7",
      skipFonts: false,
    });
  };

  const downloadPNG = async () => {
    setBusy(true);
    try {
      const dataUrl = await renderPngDataUrl();
      const link = document.createElement("a");
      link.download = `${filenameBase}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("PNG downloaded");
    } catch (e) {
      console.error(e);
      toast.error("Could not export PNG");
    } finally { setBusy(false); }
  };

  const downloadPDF = async () => {
    setBusy(true);
    try {
      const dataUrl = await renderPngDataUrl();
      let pdf;
      if (pdfFormat === "custom") {
        // Convert px @ 96dpi to mm
        const mmW = (width / 96) * 25.4;
        const mmH = (height / 96) * 25.4;
        pdf = new jsPDF({ orientation: pdfOrientation, unit: "mm", format: [mmW, mmH] });
        pdf.addImage(dataUrl, "PNG", 0, 0, mmW, mmH);
      } else {
        pdf = new jsPDF({ orientation: pdfOrientation, unit: "mm", format: pdfFormat });
        const dims = pdf.internal.pageSize;
        pdf.addImage(dataUrl, "PNG", 0, 0, dims.getWidth(), dims.getHeight());
      }
      pdf.save(`${filenameBase}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) {
      console.error(e);
      toast.error("Could not export PDF");
    } finally { setBusy(false); }
  };

  const shareWhatsApp = async () => {
    setBusy(true);
    try {
      const node = canvasRef.current;
      await waitForFonts();
      const blob = await toBlob(node, {
        cacheBust: true, pixelRatio: 2, width, height, backgroundColor: "#FDFBF7",
      });
      try {
        if (navigator.canShare && blob) {
          const file = new File([blob], `${filenameBase}.png`, { type: "image/png" });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], text: shareCaption });
            toast.success("Shared");
            setBusy(false);
            return;
          }
        }
      } catch { /* fall through */ }
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.download = `${filenameBase}.png`;
      link.href = url;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      const wa = `https://wa.me/?text=${encodeURIComponent(shareCaption + "\n\n— shared via WOWSHE")}`;
      window.open(wa, "_blank", "noopener");
      toast.success("Image downloaded — WhatsApp opened. Attach the image in your chat.");
    } catch (e) {
      console.error(e);
      toast.error("Could not share");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-5 pt-5 border-t border-aubergine/[0.08] flex flex-col gap-2">
      <button
        data-testid="studio-download-png"
        onClick={downloadPNG}
        disabled={busy}
        className="inline-flex items-center justify-center gap-2 bg-aubergine text-ivory px-5 py-3 rounded-full text-[12px] tracking-[0.14em] uppercase hover:bg-aubergine/90 disabled:opacity-50"
      >
        <FileImage className="w-4 h-4" /> Download PNG
      </button>
      {showPdf && (
        <button
          data-testid="studio-download-pdf"
          onClick={downloadPDF}
          disabled={busy}
          className="inline-flex items-center justify-center gap-2 bg-white text-aubergine border border-aubergine/25 px-5 py-3 rounded-full text-[12px] tracking-[0.14em] uppercase hover:bg-aubergine/[0.04] disabled:opacity-50"
        >
          <Download className="w-4 h-4" /> Download PDF
        </button>
      )}
      <button
        data-testid="studio-share-whatsapp"
        onClick={shareWhatsApp}
        disabled={busy}
        className="inline-flex items-center justify-center gap-2 bg-emerald-500 text-white px-5 py-3 rounded-full text-[12px] tracking-[0.14em] uppercase hover:bg-emerald-600 disabled:opacity-50"
      >
        <Share2 className="w-4 h-4" /> Share on WhatsApp
      </button>
      <p className="text-[10px] text-plumSoft/60 text-center mt-1">
        {busy ? "Preparing…" : "Personal photo & fonts render inline — safe to share."}
      </p>
    </div>
  );
}

/* ---------- utilities ---------- */

function wrapRef(component) {
  return forwardRef((props, ref) => component(props, ref));
}

function formatTime(t) {
  if (!t) return "";
  const [hh, mm] = t.split(":");
  let h = parseInt(hh, 10);
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12; if (h === 0) h = 12;
  return `${h}:${mm} ${ap}`;
}

/* ============================== BUSINESS CARD ============================== */

function BusinessCardStudio({ user }) {
  const publicUrl = `${window.location.origin}/p/${user.id}`;
  const [form, setForm] = useState({
    name: user.name || "",
    business: user.business || "",
    title: user.industry ? `${user.industry}` : "Founder",
    phone: user.phone || "",
    email: user.email || "",
    website: user.website || "",
    linkedin: user.linkedin || "",
    instagram: user.instagram || "",
    chapter: user.chapter || "",
    photo: user.avatar || "",
    profile_url: publicUrl,
  });
  const [qrDataUrl, setQrDataUrl] = useState("");
  const canvasRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(form.profile_url || publicUrl, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: 480,
      color: { dark: "#3A0F4D", light: "#FDFBF7" },
    }).then(url => { if (!cancelled) setQrDataUrl(url); }).catch(() => {});
    return () => { cancelled = true; };
  }, [form.profile_url, publicUrl]);

  return (
    <div className="grid lg:grid-cols-[400px_1fr] gap-8">
      <div className="bg-white rounded-2xl border border-aubergine/[0.08] p-6 h-fit">
        <h3 className="text-[13px] uppercase tracking-[0.14em] text-plumSoft/70 mb-5 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-champagne" /> Business Card Details
        </h3>

        <Field label="Name">
          <input data-testid="bc-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-luxe" />
        </Field>
        <Field label="Title / Role">
          <input data-testid="bc-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-luxe" placeholder="e.g. Founder & CEO" />
        </Field>
        <Field label="Business">
          <input data-testid="bc-business" value={form.business} onChange={(e) => setForm({ ...form, business: e.target.value })} className="input-luxe" />
        </Field>
        <Field label="Chapter">
          <input data-testid="bc-chapter" value={form.chapter} onChange={(e) => setForm({ ...form, chapter: e.target.value })} className="input-luxe" placeholder="e.g. Hyderabad" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone">
            <input data-testid="bc-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-luxe" />
          </Field>
          <Field label="Email">
            <input data-testid="bc-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-luxe" />
          </Field>
        </div>
        <Field label="Website">
          <input data-testid="bc-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="input-luxe" placeholder="https://…" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="LinkedIn handle">
            <input data-testid="bc-linkedin" value={form.linkedin} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} className="input-luxe" placeholder="username" />
          </Field>
          <Field label="Instagram handle">
            <input data-testid="bc-instagram" value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} className="input-luxe" placeholder="username" />
          </Field>
        </div>
        <Field label="Photo URL (optional)">
          <input data-testid="bc-photo" value={form.photo} onChange={(e) => setForm({ ...form, photo: e.target.value })} className="input-luxe" placeholder="https://… or leave blank for initials" />
        </Field>
        <Field label="QR link (your public WOWSHE profile)">
          <input data-testid="bc-url" value={form.profile_url} onChange={(e) => setForm({ ...form, profile_url: e.target.value })} className="input-luxe" />
        </Field>

        <ExportBar
          canvasRef={canvasRef}
          filenameBase={`business-card-${(form.name || "wowshe").toLowerCase().replace(/\s+/g, "-")}`}
          shareCaption={`${form.name || "A WOWSHE Sister"} — ${form.business || "WOWSHE Member"}. Connect: ${form.profile_url}`}
          width={CARD_W}
          height={CARD_H}
          pdfFormat="custom"
          pdfOrientation="landscape"
        />
      </div>

      <PreviewFrame w={CARD_W} h={CARD_H} scale={0.7}>
        <BusinessCardCanvas ref={canvasRef} data={form} qrDataUrl={qrDataUrl} />
      </PreviewFrame>
    </div>
  );
}

const BusinessCardCanvas = wrapRef(function BusinessCardCanvasInner({ data, qrDataUrl }, ref) {
  const initials = (data.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div
      ref={ref}
      style={{
        width: CARD_W, height: CARD_H,
        background: "#FDFBF7",
        fontFamily: '"Outfit", sans-serif',
        color: "#3A0F4D",
        position: "relative",
        overflow: "hidden",
        display: "grid",
        gridTemplateColumns: "1.15fr 1fr",
      }}
    >
      {/* LEFT: aubergine identity panel */}
      <div style={{
        background: "linear-gradient(160deg, #3A0F4D 0%, #5E1380 60%, #9D2B6B 100%)",
        color: "#FDFBF7",
        padding: "48px 46px",
        position: "relative",
        overflow: "hidden",
        display: "flex", flexDirection: "column", justifyContent: "space-between",
      }}>
        {/* Decorative circles */}
        <div style={{ position: "absolute", top: -80, right: -80, width: 220, height: 220, borderRadius: "50%", background: "rgba(217,165,92,0.12)" }} />
        <div style={{ position: "absolute", bottom: -100, left: -60, width: 260, height: 260, borderRadius: "50%", background: "rgba(253,251,247,0.06)" }} />

        {/* Photo circle */}
        <div style={{ position: "relative", zIndex: 2 }}>
          {data.photo ? (
            <img
              src={data.photo}
              crossOrigin="anonymous"
              alt=""
              style={{ width: 92, height: 92, borderRadius: "50%", objectFit: "cover", border: "3px solid #D9A55C", display: "block" }}
            />
          ) : (
            <div style={{
              width: 92, height: 92, borderRadius: "50%",
              background: "linear-gradient(135deg,#D9A55C,#9D2B6B)",
              color: "#FDFBF7",
              textAlign: "center", lineHeight: "92px",
              fontFamily: '"Playfair Display", serif', fontSize: 34,
              border: "3px solid #D9A55C",
            }}>{initials}</div>
          )}
        </div>

        {/* Name & role */}
        <div style={{ position: "relative", zIndex: 2 }}>
          <p style={{ margin: 0, fontFamily: '"Playfair Display", serif', fontSize: 44, lineHeight: 1.05, fontWeight: 500, letterSpacing: "-0.01em" }}>
            {data.name || "Your Name"}
          </p>
          {data.title && (
            <p style={{ margin: "10px 0 0", fontSize: 15, letterSpacing: "0.2em", textTransform: "uppercase", color: "#E7BC7E" }}>{data.title}</p>
          )}
          {data.business && (
            <p style={{ margin: "8px 0 0", fontSize: 16, color: "rgba(253,251,247,0.85)" }}>{data.business}</p>
          )}
        </div>

        {/* WOWSHE mark */}
        <div style={{ position: "relative", zIndex: 2, display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 32, height: 1, background: "#D9A55C" }} />
          <p style={{ margin: 0, fontSize: 10, letterSpacing: "0.32em", textTransform: "uppercase", color: "#E7BC7E" }}>
            Proud WOWSHE Member{data.chapter ? ` · ${data.chapter}` : ""}
          </p>
        </div>
      </div>

      {/* RIGHT: contact + QR */}
      <div style={{ padding: "48px 46px", display: "flex", flexDirection: "column", justifyContent: "space-between", position: "relative" }}>
        {/* Ornamental frame */}
        <div style={{ position: "absolute", top: 24, right: 24, bottom: 24, left: 24, border: "1px solid rgba(217,165,92,0.4)", pointerEvents: "none", borderRadius: 4 }} />

        <div style={{ position: "relative", zIndex: 2 }}>
          <p style={{ margin: 0, fontSize: 10, letterSpacing: "0.35em", textTransform: "uppercase", color: "#9D2B6B", fontWeight: 500 }}>Get In Touch</p>
          <div style={{ marginTop: 20, display: "grid", rowGap: 14 }}>
            {data.phone && <ContactRow label="Phone" value={data.phone} />}
            {data.email && <ContactRow label="Email" value={data.email} />}
            {data.website && <ContactRow label="Website" value={data.website.replace(/^https?:\/\//, "")} />}
            {data.linkedin && <ContactRow label="LinkedIn" value={`in/${data.linkedin}`} />}
            {data.instagram && <ContactRow label="Instagram" value={`@${data.instagram}`} />}
          </div>
        </div>

        {/* QR + WOWSHE logo */}
        <div style={{ position: "relative", zIndex: 2, display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16 }}>
          <div>
            <img src="/wowshe-logo.png" crossOrigin="anonymous" alt="WOWSHE" style={{ height: 42, width: "auto", display: "block" }} />
            <p style={{ margin: "8px 0 0", fontSize: 9, letterSpacing: "0.28em", textTransform: "uppercase", color: "#6B4769" }}>wowshe.in</p>
          </div>
          {qrDataUrl && (
            <div style={{ textAlign: "center" }}>
              <img src={qrDataUrl} alt="QR" style={{ width: 130, height: 130, display: "block", borderRadius: 8 }} />
              <p style={{ margin: "6px 0 0", fontSize: 9, letterSpacing: "0.22em", textTransform: "uppercase", color: "#9D2B6B" }}>Scan to connect</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

function ContactRow({ label, value }) {
  return (
    <div>
      <p style={{ margin: 0, fontSize: 9, letterSpacing: "0.28em", textTransform: "uppercase", color: "#9D2B6B" }}>{label}</p>
      <p style={{ margin: "3px 0 0", fontSize: 15, color: "#3A0F4D", wordBreak: "break-word" }}>{value}</p>
    </div>
  );
}
